import { toast } from 'react-toastify';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { deleteDataCatalog, deleteDataCatalogEntry, IDataCatalogEntry, IDataCatalogOption, TDataCatalogEntryType } from '@verdocs/js-sdk';
import { saveDataCatalogEntries, updateDataCatalogEntry, updateOrganization, VerdocsEndpoint, withdrawDataCatalogSubmission } from '@verdocs/js-sdk';
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  EllipsisVerticalIcon,
  LockClosedIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../Components/DropdownMenu';
import { invalidateDataCatalogs, useDataCatalog, useOrganizationMembers } from '../../react-sdk/queries';
import { Badge, Button, FIELD_CLASSES, SegmentedControl, Spinner, Switch } from '../../Components';
import { ReleaseDataCatalogDialog } from '../../Dialogs/ReleaseDataCatalogDialog';
import { ImportDataCatalogDialog } from '../../Dialogs/ImportDataCatalogDialog';
import { SubmitDataCatalogDialog } from '../../Dialogs/SubmitDataCatalogDialog';
import { ShareDataCatalogDialog } from '../../Dialogs/ShareDataCatalogDialog';
import { EditDataCatalogDialog } from '../../Dialogs/EditDataCatalogDialog';
import { OkCancelDialog } from '../../Dialogs/OkCancelDialog';
import { useRole } from '../../hooks/useRole';
import { AppState } from '../../AppState';
import { URLS } from '../../Routing/urls';

const TYPES: { key: TDataCatalogEntryType; label: string }[] = [
  { key: 'text', label: 'Text' },
  { key: 'date', label: 'Date' },
  { key: 'boolean', label: 'Yes/No' },
  { key: 'choice', label: 'Choice' },
];

const TYPE_LABELS = Object.fromEntries(TYPES.map(type => [ type.key, type.label ])) as Record<TDataCatalogEntryType, string>;

const SOURCES = { scratch: 'From scratch', import: 'Imported', marketplace: 'From the marketplace' };

const EXAMPLE_PLACEHOLDERS: Record<TDataCatalogEntryType, string> = {
  text: 'Jordan Rivera',
  date: '2027-03-31',
  boolean: 'Yes',
  choice: 'OH',
};

// One option per line, "value = label", or just a value when the label matches it
const optionsToText = (options: IDataCatalogOption[] | null) =>
  (options || []).map(option => (option.label === option.value ? option.value : `${option.value} = ${option.label}`)).join('\n');

const textToOptions = (text: string): IDataCatalogOption[] =>
  text
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => {
      const [ value, ...label ] = line.split('=');
      return { value: value!.trim(), label: label.join('=').trim() || value!.trim() };
    });

interface IEntryDraft {
  key: string;
  label: string;
  type: TDataCatalogEntryType;
  description: string;
  example: string;
  options: string;
  sensitive: boolean;
}

const draftFrom = (entry?: IDataCatalogEntry): IEntryDraft => ({
  key: entry?.key || '',
  label: entry?.label || '',
  type: entry?.type || 'text',
  description: entry?.description || '',
  example: entry?.example || '',
  options: optionsToText(entry?.options || null),
  sensitive: !!entry?.sensitive,
});

interface EntryEditorProps {
  entry?: IDataCatalogEntry;
  saving: boolean;
  onSave: (draft: IEntryDraft) => void;
  onCancel: () => void;
  onDelete?: () => void;
}

function EntryEditor({ entry, saving, onSave, onCancel, onDelete }: EntryEditorProps) {
  const [ draft, setDraft ] = useState(draftFrom(entry));
  const set = (changes: Partial<IEntryDraft>) => setDraft({ ...draft, ...changes });
  const valid = !!draft.label.trim() &&
    (entry || /^[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+)*$/.test(draft.key)) &&
    (draft.type !== 'choice' || !!draft.options.trim());

  return (
    <div className="my-1.5 rounded-lg border-[1.5px] border-indigo-300 bg-[#fbfbff] px-3.5 py-3">
      {entry ? (
        <div className="mb-2.5 flex items-center gap-2">
          <span className="font-mono text-[12.5px] text-indigo-700">{entry.key}</span>
        </div>
      ) : (
        <div className="mb-3">
          <label className="mb-1 block text-[12.5px] font-medium text-[#33364b]">Key</label>
          <input
            autoFocus
            value={draft.key}
            onChange={e => set({ key: e.target.value.trim() })}
            placeholder="applicant.full_name"
            className={`${FIELD_CLASSES} font-mono`}
          />
          <div className="mt-1 text-[11.5px] text-gray-500">
            Letters, digits, &quot;_&quot;, and &quot;-&quot;, with &quot;.&quot; between levels.
          </div>
        </div>
      )}

      <div className="grid grid-cols-[1.3fr_1fr] gap-3">
        <div>
          <label className="mb-1 block text-[12.5px] font-medium text-[#33364b]">Label</label>
          <input
            value={draft.label}
            onChange={e => set({ label: e.target.value })}
            placeholder="Applicant name"
            className={FIELD_CLASSES}
          />
        </div>
        <div>
          <label className="mb-1 block text-[12.5px] font-medium text-[#33364b]">Example</label>
          <input
            value={draft.example}
            onChange={e => set({ example: e.target.value })}
            placeholder={draft.sensitive ? 'Kept out of assistant answers' : EXAMPLE_PLACEHOLDERS[draft.type]}
            className={FIELD_CLASSES}
          />
        </div>
      </div>

      <div className="mt-3">
        <label className="mb-1 block text-[12.5px] font-medium text-[#33364b]">Type</label>
        <SegmentedControl
          options={TYPES}
          value={draft.type}
          onChange={key => set({ type: key as TDataCatalogEntryType })}
        />
      </div>

      {draft.type === 'choice' && (
        <div className="mt-3">
          <label className="mb-1 block text-[12.5px] font-medium text-[#33364b]">Options</label>
          <textarea
            value={draft.options}
            onChange={e => set({ options: e.target.value })}
            rows={4}
            placeholder={'OH = Ohio\nTX = Texas'}
            className="w-full rounded-md border border-[#dcdee5] px-3 py-2 font-mono text-[12.5px] text-[#33364b] outline-none placeholder:text-gray-300 focus:border-[#654dcb]"
          />
          <div className="mt-1 text-[11.5px] text-gray-500">
            One per line: the value a sender&apos;s data uses, then &quot;=&quot; and the label signers see.
          </div>
        </div>
      )}

      <div className="mt-3">
        <label className="mb-1 block text-[12.5px] font-medium text-[#33364b]">Description</label>
        <input
          value={draft.description}
          onChange={e => set({ description: e.target.value })}
          className={FIELD_CLASSES}
        />
      </div>

      <label className="mt-3 flex cursor-pointer items-center gap-2.5">
        <Switch
          checked={draft.sensitive}
          onCheckedChange={sensitive => set({ sensitive })}
          className="data-[state=checked]:bg-[#654dcb] data-[state=unchecked]:bg-gray-200"
        />
        <span className="text-[12.5px] text-[#33364b]">Sensitive</span>
        <span className="text-[11.5px] text-gray-500">Keeps the example out of assistant answers and AI prompts.</span>
      </label>

      <div className="mt-3 flex items-center gap-2">
        {onDelete && (
          <button
            onClick={onDelete}
            className="text-[12.5px] text-red-600 hover:underline"
            disabled={saving}>
            Delete key
          </button>
        )}
        <span className="flex-1" />
        {saving && <Spinner mode="dark" />}
        <Button
          size="xsmall"
          variant="secondary"
          label="Cancel"
          onClick={onCancel}
          disabled={saving}
        />
        <Button
          size="xsmall"
          label={entry ? 'Save' : 'Add key'}
          onClick={() => onSave(draft)}
          disabled={!valid || saving}
        />
      </div>
    </div>
  );
}

function EntryRow({ entry, suffix, onOpen }: { entry: IDataCatalogEntry; suffix: string; onOpen: () => void }) {
  return (
    <button
      onClick={onOpen}
      className="flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left hover:bg-gray-50">
      <span className="w-[230px] shrink-0 truncate text-[13px] text-[#33364b]">{entry.label}</span>
      <span className="w-[210px] shrink-0 truncate font-mono text-[12px] text-indigo-700">{suffix}</span>
      <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-500">{TYPE_LABELS[entry.type]}</span>
      <span className="min-w-0 flex-1 truncate text-[12.5px] text-gray-500">
        {entry.sensitive ? (
          <span className="inline-flex items-center gap-1 text-gray-400">
            <LockClosedIcon className="size-3" />
            Hidden
          </span>
        ) : entry.type === 'choice' ? (
          (entry.options || []).map(option => option.value).join(', ')
        ) : (
          entry.example
        )}
      </span>
    </button>
  );
}

export function DataCatalogEditor() {
  const navigate = useNavigate();
  const { catalogId = '' } = useParams();
  const organizationId = AppState.currentProfile?.organization_id || '';
  const catalogQuery = useDataCatalog(organizationId, catalogId);
  const membersQuery = useOrganizationMembers(organizationId);
  const { isAdmin } = useRole();
  const [ openKey, setOpenKey ] = useState<string | null>(null);
  const [ adding, setAdding ] = useState(false);
  const [ saving, setSaving ] = useState(false);
  const [ search, setSearch ] = useState('');
  const [ collapsed, setCollapsed ] = useState<Record<string, boolean>>({});
  const [ dialog, setDialog ] = useState<'details' | 'share' | 'release' | 'delete' | 'import' | 'submit' | null>(null);
  const [ deletingKey, setDeletingKey ] = useState<string | null>(null);

  const catalog = catalogQuery.data;
  if (!catalog) {
    return (
      <div className="flex flex-1 items-center justify-center">
        {catalogQuery.isError ? (
          <div className="text-[13px] text-gray-500">This catalog couldn&apos;t be loaded.</div>
        ) : (
          <Spinner mode="dark" />
        )}
      </div>
    );
  }

  const entries = catalog.entries || [];
  const proposals = entries.filter(entry => entry.status === 'proposed');
  const query = search.trim().toLowerCase();
  const live = entries
    .filter(entry => entry.status === 'active')
    .filter(entry => !query || `${entry.key} ${entry.label}`.toLowerCase().includes(query));
  const groups = live.reduce<Record<string, IDataCatalogEntry[]>>((all, entry) => {
    const prefix = entry.key.includes('.') ? entry.key.split('.')[0]! : '';
    (all[prefix] ||= []).push(entry);
    return all;
  }, {});
  const canRelease = !!catalog.has_unreleased_changes;
  const submission = catalog.submission;
  // Submitting releases the current keys first when they aren't released yet
  const needsRelease = catalog.latest_version === 0 || !!catalog.has_unreleased_changes;
  const submitVersion = needsRelease ? catalog.latest_version + 1 : catalog.latest_version;
  const canSubmit = entries.some(entry => entry.status === 'active') && submission?.status !== 'submitted' && catalog.public_version !== submitVersion;
  const proposerName = (profileId: string | null) => {
    const member = (membersQuery.data || []).find(candidate => candidate.id === profileId);
    return member ? `${member.first_name} ${member.last_name}` : 'a member';
  };

  const refresh = () => {
    setDialog(null);
    invalidateDataCatalogs();
  };

  const run = async (action: () => Promise<unknown>, success: string, failure: string) => {
    setSaving(true);
    try {
      await action();
      invalidateDataCatalogs();
      toast.success(success);
      setSaving(false);
      return true;
    } catch (e: any) {
      toast.error(`${failure}: ${e.response?.data?.message || e.message}`);
      setSaving(false);
      return false;
    }
  };

  const fieldsFrom = (draft: IEntryDraft) => ({
    label: draft.label.trim(),
    type: draft.type,
    description: draft.description.trim() || null,
    example: draft.example.trim() || null,
    options: draft.type === 'choice' ? textToOptions(draft.options) : null,
    sensitive: draft.sensitive,
  });

  const handleAdd = async (draft: IEntryDraft) => {
    const ok = await run(
      () => saveDataCatalogEntries(VerdocsEndpoint.getDefault(), organizationId, catalog.id, [ { key: draft.key, ...fieldsFrom(draft) } ]),
      `Added ${draft.key}`,
      'Error adding the key',
    );
    if (ok) setAdding(false);
  };

  const handleUpdate = async (entry: IDataCatalogEntry, draft: IEntryDraft) => {
    const ok = await run(
      () => updateDataCatalogEntry(VerdocsEndpoint.getDefault(), organizationId, catalog.id, entry.key, fieldsFrom(draft)),
      `Saved ${entry.key}`,
      'Error saving the key',
    );
    if (ok) setOpenKey(null);
  };

  const handleDeleteKey = async () => {
    if (!deletingKey) return;
    const key = deletingKey;
    setDeletingKey(null);
    const ok = await run(
      () => deleteDataCatalogEntry(VerdocsEndpoint.getDefault(), organizationId, catalog.id, key),
      `Deleted ${key}`,
      'Error deleting the key',
    );
    if (ok) setOpenKey(null);
  };

  const handleSetDefault = () =>
    run(
      () => updateOrganization(VerdocsEndpoint.getDefault(), organizationId, { default_data_catalog_id: catalog.id }),
      `${catalog.name} is now the default catalog`,
      'Error setting the default catalog',
    );

  const handleDeleteCatalog = async () => {
    setDialog(null);
    const ok = await run(
      () => deleteDataCatalog(VerdocsEndpoint.getDefault(), organizationId, catalog.id),
      `${catalog.name} deleted`,
      'Error deleting the catalog',
    );
    if (ok) await navigate(URLS.SETTINGS_DATA_CATALOGS.build());
  };

  return (
    <div className="flex flex-1 flex-col box-border overflow-y-scroll">
      <div className="w-full max-w-[1000px] pb-10">
        <button
          onClick={() => navigate(URLS.SETTINGS_DATA_CATALOGS.build())}
          className="mb-3 flex items-center gap-1.5 text-[13px] text-indigo-600 hover:underline">
          <ArrowLeftIcon className="size-3.5" />
          Data Catalogs
        </button>

        <div className="mb-4 flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl text-[#33364b]">{catalog.name}</h1>
              {catalog.is_default && (
                <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wider text-indigo-600">
                  Default
                </span>
              )}
            </div>
            {catalog.description && <div className="mt-0.5 text-[13px] text-gray-500">{catalog.description}</div>}
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-gray-500">
              <span>{SOURCES[catalog.source]}</span>
              <span>{catalog.sharing === 'shared' ? 'Shared with sub-accounts' : 'Private'}</span>
              {catalog.latest_version > 0 && <span>Version {catalog.latest_version} released</span>}
              {!!catalog.public_version && <span>Public, version {catalog.public_version}</span>}
              {catalog.has_unreleased_changes && <Badge tone="warn">Unreleased changes</Badge>}
            </div>
          </div>

          <Button
            size="small"
            variant="secondary"
            label="Share settings"
            onClick={() => setDialog('share')}
          />
          {canRelease && (
            <Button
              size="small"
              label={`Release version ${catalog.latest_version + 1}`}
              onClick={() => setDialog('release')}
            />
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="mt-1 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                aria-label="Catalog actions">
                <EllipsisVerticalIcon className="size-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white">
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => setDialog('details')}>
                Edit name and description
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer"
                disabled={catalog.is_default}
                onClick={handleSetDefault}>
                Set as default
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem
                  className="cursor-pointer"
                  disabled={!canSubmit}
                  onClick={() => setDialog('submit')}>
                  Submit to the public marketplace
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                className="cursor-pointer text-red-600 focus:text-red-600"
                onClick={() => setDialog('delete')}>
                Delete catalog
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {catalog.update_available && (
          <div className="mb-4 flex items-center gap-3 rounded-lg border border-[#f0d9b5] bg-[#fdf8ef] px-3.5 py-2.5 text-[13px] text-[#95591a]">
            <span className="flex-1">A newer version of this catalog is available from its publisher.</span>
            <Button
              size="xsmall"
              label="Review update"
              onClick={() => navigate(URLS.SETTINGS_DATA_CATALOG_UPDATE.build(catalog.id))}
            />
          </div>
        )}

        {submission?.status === 'submitted' && (
          <div className="mb-4 flex items-center gap-3 rounded-lg border border-[#d9d2fb] bg-[#f8f7fe] px-3.5 py-2.5 text-[13px] text-[#4b3fa0]">
            <span className="flex-1">Version {submission.version} is in review for the public marketplace.</span>
            {isAdmin && (
              <Button
                size="xsmall"
                variant="secondary"
                label="Withdraw"
                disabled={saving}
                onClick={() =>
                  run(
                    () => withdrawDataCatalogSubmission(VerdocsEndpoint.getDefault(), organizationId, catalog.id),
                    'Withdrew the submission',
                    'Error withdrawing the submission',
                  )}
              />
            )}
          </div>
        )}

        {submission?.status === 'rejected' && (
          <div className="mb-4 flex items-center gap-3 rounded-lg border border-[#f0d9b5] bg-[#fdf8ef] px-3.5 py-2.5 text-[13px] text-[#95591a]">
            <span className="flex-1">
              Version {submission.version} wasn&apos;t approved for the public marketplace: {submission.review_notes}
            </span>
            {isAdmin && canSubmit && (
              <Button
                size="xsmall"
                label="Submit again"
                onClick={() => setDialog('submit')}
              />
            )}
          </div>
        )}

        {submission?.status !== 'submitted' && submission?.status !== 'rejected' && !!catalog.public_version && catalog.latest_version > catalog.public_version && (
          <div className="mb-4 flex items-center gap-3 rounded-lg border border-[#ebecf0] bg-[#fafafb] px-3.5 py-2.5 text-[13px] text-gray-600">
            <span className="flex-1">
              Version {catalog.public_version} is public. Version {catalog.latest_version} stays private until you submit it.
            </span>
            {isAdmin && canSubmit && (
              <Button
                size="xsmall"
                label={`Submit version ${submitVersion}`}
                onClick={() => setDialog('submit')}
              />
            )}
          </div>
        )}

        {proposals.length > 0 && (
          <>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-700">Waiting for approval</h2>
            <div className="mb-5 flex flex-col gap-2">
              {proposals.map(entry => (
                <div
                  key={entry.key}
                  className="flex items-center gap-3 rounded-lg border border-[#f0d9b5] bg-[#fdf8ef] px-3.5 py-2.5">
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium text-[#33364b]">
                      {entry.label} <span className="ml-1.5 font-mono text-[12px] font-normal text-indigo-700">{entry.key}</span>
                    </div>
                    <div className="mt-0.5 text-[12px] text-gray-500">
                      {TYPE_LABELS[entry.type]} · proposed by {proposerName(entry.proposed_by)}
                    </div>
                  </div>
                  <Button
                    size="xsmall"
                    label="Approve"
                    disabled={saving}
                    onClick={() =>
                      run(
                        () =>
                          updateDataCatalogEntry(VerdocsEndpoint.getDefault(), organizationId, catalog.id, entry.key, { status: 'active' }),
                        `Approved ${entry.key}`,
                        'Error approving the key',
                      )}
                  />
                  <button
                    disabled={saving}
                    className="px-1.5 text-[12.5px] text-gray-500 hover:text-gray-700"
                    onClick={() =>
                      run(
                        () => deleteDataCatalogEntry(VerdocsEndpoint.getDefault(), organizationId, catalog.id, entry.key),
                        `Rejected ${entry.key}`,
                        'Error rejecting the key',
                      )}>
                    Reject
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="mb-2 flex items-center gap-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">Keys</h2>
          <span className="text-[12px] text-gray-400">{entries.filter(entry => entry.status === 'active').length}</span>
          <span className="flex-1" />
          <div className="w-[220px]">
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search keys"
              className={FIELD_CLASSES}
            />
          </div>
          <button
            onClick={() => {
              setAdding(true);
              setOpenKey(null);
            }}
            className="inline-flex items-center gap-1 whitespace-nowrap text-[13px] font-medium text-indigo-600 hover:underline">
            <PlusIcon className="size-4" />
            Add key
          </button>
          <button
            onClick={() => setDialog('import')}
            className="inline-flex items-center gap-1 whitespace-nowrap text-[13px] font-medium text-indigo-600 hover:underline">
            <ArrowDownTrayIcon className="size-4" />
            Import keys
          </button>
        </div>

        {adding && (
          <EntryEditor
            saving={saving}
            onSave={handleAdd}
            onCancel={() => setAdding(false)}
          />
        )}

        {live.length === 0 && !adding && (
          <div className="rounded-lg border border-dashed border-[#dcdee5] bg-[#fbfbfc] p-6 text-center text-[13px] text-gray-500">
            {query ? 'No keys match.' : 'No keys yet. Add one, or import them from sample JSON, a schema, a CSV header row, or a shared catalog.'}
          </div>
        )}

        {Object.entries(groups)
          .sort(([ a ], [ b ]) => a.localeCompare(b))
          .map(([ prefix, groupEntries ]) => (
            <div
              key={prefix || '_'}
              className="mb-2">
              {prefix && (
                <button
                  onClick={() => setCollapsed({ ...collapsed, [prefix]: !collapsed[prefix] })}
                  className="flex items-center gap-1.5 py-1">
                  {collapsed[prefix] ? (
                    <ChevronRightIcon className="size-3.5 text-gray-400" />
                  ) : (
                    <ChevronDownIcon className="size-3.5 text-gray-400" />
                  )}
                  <span className="font-mono text-[13px] font-semibold text-[#33364b]">{prefix}</span>
                  <span className="text-[12px] text-gray-400">
                    {groupEntries.length} {groupEntries.length === 1 ? 'key' : 'keys'}
                  </span>
                </button>
              )}
              {!collapsed[prefix] && (
                <div className={prefix ? 'ml-[7px] border-l-2 border-[#ebecf0] pl-3.5' : ''}>
                  {groupEntries.map(entry =>
                    openKey === entry.key ? (
                      <EntryEditor
                        key={entry.key}
                        entry={entry}
                        saving={saving}
                        onSave={draft => handleUpdate(entry, draft)}
                        onCancel={() => setOpenKey(null)}
                        onDelete={() => setDeletingKey(entry.key)}
                      />
                    ) : (
                      <EntryRow
                        key={entry.key}
                        entry={entry}
                        suffix={prefix ? entry.key.slice(prefix.length + 1) : entry.key}
                        onOpen={() => {
                          setOpenKey(entry.key);
                          setAdding(false);
                        }}
                      />
                    ),
                  )}
                </div>
              )}
            </div>
          ))}
      </div>

      {dialog === 'details' && (
        <EditDataCatalogDialog
          catalog={catalog}
          onClose={() => setDialog(null)}
          onSaved={refresh}
        />
      )}
      {dialog === 'share' && (
        <ShareDataCatalogDialog
          catalog={catalog}
          onClose={() => setDialog(null)}
          onSaved={refresh}
          submitVersion={submitVersion}
          onSubmit={isAdmin && canSubmit ? () => setDialog('submit') : undefined}
        />
      )}
      {dialog === 'submit' && (
        <SubmitDataCatalogDialog
          catalog={catalog}
          version={submitVersion}
          release={needsRelease}
          onClose={() => setDialog(null)}
          onSubmitted={refresh}
        />
      )}
      {dialog === 'release' && (
        <ReleaseDataCatalogDialog
          catalog={catalog}
          onClose={() => setDialog(null)}
          onReleased={refresh}
        />
      )}
      {dialog === 'import' && (
        <ImportDataCatalogDialog
          catalog={catalog}
          onClose={() => setDialog(null)}
          onDone={refresh}
        />
      )}
      {dialog === 'delete' && (
        <OkCancelDialog
          onOk={handleDeleteCatalog}
          onCancel={() => setDialog(null)}>
          <p className="mb-6">
            Delete the catalog <strong>{catalog.name}</strong>?{' '}
            {catalog.is_default && 'It\'s your default, so new templates won\'t have a catalog until you pick a new default. '}
            This can&apos;t be undone.
          </p>
        </OkCancelDialog>
      )}
      {deletingKey && (
        <OkCancelDialog
          onOk={handleDeleteKey}
          onCancel={() => setDeletingKey(null)}>
          <p className="mb-6">
            Delete the key <strong>{deletingKey}</strong>? Template fields bound to it keep their data key.
          </p>
        </OkCancelDialog>
      )}
    </div>
  );
}
